import React, { useState, lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ShieldCheckIcon, LockIcon, ZapIcon } from 'lucide-react';
import { includes, MEGA_PACK_CHECKOUT_URL, MEGA_PACK_COCINA_CHECKOUT_URL, PRODUCT_IMAGE } from '../data/content';
type OfferId = 'mega-pack' | 'mega-pack-cocina';
interface Offer {
  id: OfferId;
  label: string;
  title: string;
  description: string;
  price: string;
  checkoutUrl: string;
  cta: string;
}
const offers: Offer[] = [{
  id: 'mega-pack',
  label: 'Opción esencial',
  title: 'Mega Pack Tiroides',
  description: 'Los 9 tomos completos, más de 100 recetas, herramientas de organización y los 5 bonos especiales.',
  price: '$19.990',
  checkoutUrl: MEGA_PACK_CHECKOUT_URL,
  cta: 'QUIERO MI MEGA PACK AHORA'
}, {
  id: 'mega-pack-cocina',
  label: 'La oferta más completa',
  title: 'Mega Pack + La Cocina sin Límites',
  description: 'Todo el Mega Pack Tiroides, más 4 módulos y +69 preparaciones para que comer rico nunca vuelva a ser un problema.',
  price: '$24.990',
  checkoutUrl: MEGA_PACK_COCINA_CHECKOUT_URL,
  cta: 'QUIERO EL PACK COMPLETO AHORA'
}];
const cocinaModules = [{
  name: 'Pan de Verdad',
  description: 'Pan de taza en 3 minutos, pan de molde de semillas para toda la semana, focaccia para compartir y bruschettas que parecen de restaurante. El desayuno que extrañabas, de vuelta en tu mesa.'
}, {
  name: 'Repostería Secreta',
  description: 'Chocolates, bombones, mousses, tortas de cumpleaños reales, alfajores y postres de emergencia en 2 minutos. El dulce sin azúcar, sin lácteos inflamatorios y sin culpa.'
}, {
  name: 'El Rey de la Tabla',
  description: 'Mayonesa casera, kétchup real, hummus gourmet, chips crocantes, dips y quesos untables. La picada del sábado completa, sin un solo ultraprocesado.'
}, {
  name: 'El Secreto del Umami',
  description: 'Caldos de huesos concentrados, el “Polvo Mágico” que reemplaza al Knorr, aceites infusionados, sazonadores caseros y fermentos que transforman cualquier plato simple en algo memorable.'
}];
export function PurchaseSection() {
  const [selectedOfferId, setSelectedOfferId] = useState<OfferId>('mega-pack-cocina');
  const selectedOffer = offers.find(offer => offer.id === selectedOfferId) ?? offers[0];
  return <section id="comprar" className="scroll-mt-4 bg-sage-deep py-20 sm:py-28" aria-labelledby="comprar-title" data-id="element-94">
      <div className="mx-auto max-w-5xl px-5" data-id="element-95">
        <div className="text-center" data-id="element-96">
          <h2 id="comprar-title" className="font-serif text-3xl font-semibold text-cream sm:text-5xl" data-id="element-97">
            Elegí la opción que mejor se adapta a vos
          </h2>
          <p className="mt-4 text-lg text-cream/80" data-id="element-98">
            Las dos opciones incluyen acceso digital inmediato y de por vida.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.55
      }} className="mt-10 overflow-hidden rounded-[2rem] bg-cream shadow-2xl shadow-ink/30" data-id="element-99">
          <div className="grid gap-6 p-6 sm:grid-cols-[200px_1fr] sm:p-8" data-id="element-100">
            <img src={PRODUCT_IMAGE} alt="Mega Pack Tiroides — colección completa de 9 tomos" className="mx-auto w-48 rounded-2xl shadow-lg sm:mx-0 sm:w-full" loading="lazy" data-id="element-101" />
            <div data-id="element-102">
              <p className="font-serif text-xl font-semibold text-ink" data-id="element-103">
                Mega Pack Tiroides incluye:
              </p>
              <ul className="mt-4 grid gap-2.5 sm:grid-cols-2" data-id="element-104">
                {includes.map(item => <li key={item} className="flex items-start gap-3" data-id="element-105">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage/20" data-id="element-106">
                      <CheckIcon className="h-3.5 w-3.5 text-sage-deep" aria-hidden="true" data-id="element-107" />
                    </span>
                    <span className="text-[14px] leading-snug text-ink/85" data-id="element-108">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="border-t border-sage/20 bg-cream-deep p-6 sm:p-8" data-id="element-109">
            <div className="text-center" data-id="element-110">
              <span className="inline-flex items-center gap-2 rounded-full bg-coral/15 px-4 py-1.5 text-sm font-bold uppercase tracking-wide text-coral-deep" data-id="element-111">
                <ZapIcon className="h-4 w-4" aria-hidden="true" data-id="element-112" />
                Oferta por tiempo limitado
              </span>
              <h3 className="mt-4 font-serif text-2xl font-semibold text-ink sm:text-3xl" data-id="element-113">
                Elegí tu acceso
              </h3>
            </div>

            <div role="radiogroup" aria-label="Elegí tu oferta" className="mt-7 grid gap-4 lg:grid-cols-2" data-id="element-114">
              {offers.map(offer => {
              const isSelected = selectedOfferId === offer.id;
              const isPremium = offer.id === 'mega-pack-cocina';
              return <button key={offer.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelectedOfferId(offer.id)} className={`relative rounded-3xl border-2 p-6 text-left transition-all duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/30 ${isSelected ? 'border-coral bg-white shadow-lg shadow-coral/10' : 'border-sage/25 bg-white/60 hover:border-sage/60'}`} data-id="element-115">
                    {isPremium && <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-coral px-4 py-1 text-xs font-bold uppercase tracking-wide text-white shadow" data-id="element-116">
                        Mejor valor
                      </span>}
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${isSelected ? 'border-coral bg-coral' : 'border-sage/50 bg-transparent'}`} aria-hidden="true" data-id="element-117">
                      {isSelected && <CheckIcon className="h-3.5 w-3.5 text-white" data-id="element-118" />}
                    </span>
                    <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-coral-deep" data-id="element-119">
                      {offer.label}
                    </p>
                    <p className="mt-2 font-serif text-2xl font-semibold leading-tight text-ink" data-id="element-120">
                      {offer.title}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-ink/70" data-id="element-121">
                      {offer.description}
                    </p>
                    <p className="mt-5 font-serif text-3xl font-bold text-ink" data-id="element-122">
                      {offer.price}{' '}
                      <span className="text-lg font-medium text-ink/50" data-id="element-123">
                        ARS
                      </span>
                    </p>
                    <p className="mt-1 text-xs font-medium text-ink/55" data-id="element-124">
                      Pago único · Acceso de por vida
                    </p>
                  </button>;
            })}
            </div>

            {selectedOfferId === 'mega-pack-cocina' && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} transition={{
            duration: 0.35
          }} className="mt-7 overflow-hidden rounded-3xl border border-coral/25 bg-white p-6 sm:p-8" data-id="element-125">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-coral-deep" data-id="element-126">
                  🎁 Lo que incluye “La Cocina sin Límites”
                </p>
                <h4 className="mt-2 font-serif text-2xl font-semibold text-ink" data-id="element-127">
                  4 módulos. +69 preparaciones completas.
                </h4>
                <p className="mt-2 text-[15px] leading-relaxed text-ink/70" data-id="element-128">
                  Todo lo que necesitás para que comer rico nunca más sea un
                  problema.
                </p>
                <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-129">
                  {cocinaModules.map(module => <article key={module.name} data-id="element-130">
                      <h5 className="font-serif text-lg font-semibold text-sage-deep" data-id="element-131">
                        {module.name}
                      </h5>
                      <p className="mt-1.5 text-sm leading-relaxed text-ink/70" data-id="element-132">
                        {module.description}
                      </p>
                    </article>)}
                </div>
                <p className="mt-6 border-t border-sage/15 pt-5 text-sm font-semibold leading-relaxed text-ink" data-id="element-133">
                  Todo con ingredientes que conseguís en cualquier super o
                  dietética argentina. Sin gastar de más. Sin complicaciones.
                </p>
              </motion.div>}

            <div className="mt-8 text-center" data-id="element-134">
              <p className="text-sm font-medium uppercase tracking-widest text-ink/50" data-id="element-135">
                Total
              </p>
              <p className="mt-1 font-serif text-5xl font-bold text-ink sm:text-6xl" data-id="element-136">
                {selectedOffer.price}{' '}
                <span className="text-2xl font-medium text-ink/50" data-id="element-137">ARS</span>
              </p>
              <p className="mt-1 text-sm text-ink/60" data-id="element-138">
                Pago único · Acceso de por vida
              </p>

              <a href={selectedOffer.checkoutUrl} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-coral px-8 py-5 text-lg font-bold tracking-wide text-white shadow-xl shadow-coral/30 transition-all duration-200 hover:bg-coral-deep active:scale-[0.99] focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/30 sm:text-xl" data-id="element-139">
                {selectedOffer.cta}
              </a>

              <p className="mt-4 flex items-center justify-center gap-1.5 text-sm font-medium text-sage-deep" data-id="element-140">
                <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-141" />
                Acceso inmediato y de por vida
              </p>
            </div>
          </div>
        </motion.div>

        <div className="mt-8 flex flex-col items-center gap-4 rounded-3xl border border-cream/25 bg-sage-deep/40 p-6 text-center sm:flex-row sm:text-left" data-id="element-142">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-cream/15" data-id="element-143">
            <ShieldCheckIcon className="h-7 w-7 text-cream" aria-hidden="true" data-id="element-144" />
          </div>
          <div data-id="element-145">
            <p className="font-serif text-xl font-semibold text-cream" data-id="element-146">
              Garantía
            </p>
            <p className="mt-1 text-[15px] leading-relaxed text-cream/80" data-id="element-147">
              Si después de revisarlo sentís que no es lo que esperabas, te
              devolvemos el dinero. Sin preguntas. Sin trámites. Sin vueltas.
            </p>
          </div>
        </div>
      </div>
    </section>;
}